import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateCustomVerificationTypeMutation } from "@/data/mutations";
import { useDialogState } from "@/hooks/use-dialog-state";

const createVerificationTypeFormSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(75, "Name must be less than 75 characters"),
  description: z.string().optional(),
});

export const CreateVerificationTypeDialog = () => {
  const { dialog, closeDialog } = useDialogState();

  const createVerificationTypeMutation =
    useCreateCustomVerificationTypeMutation();
  const form = useForm({
    resolver: zodResolver(createVerificationTypeFormSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const handleSubmit = (
    values: z.infer<typeof createVerificationTypeFormSchema>,
  ) => {
    createVerificationTypeMutation.mutate({
      name: values.name,
      description: values.description,
    });
  };

  return (
    <Dialog
      open={dialog?.name === "create-verification-type"}
      onOpenChange={(open) => {
        if (!open) closeDialog();
      }}
    >
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <DialogHeader>
              <DialogTitle>Create Verification Type</DialogTitle>
              <DialogDescription>
                Create a new verification type for your organization.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 my-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Verification Type*</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Sent to warehouse"
                        {...field}
                        autoFocus
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="This verification happens when the item is finished product and sent to the warehouse for storage."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                disabled={createVerificationTypeMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createVerificationTypeMutation.isPending}
              >
                {createVerificationTypeMutation.isPending ? (
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Create
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
